import { useEffect, useState } from 'react';
import type { ComposeIntent, Dest } from '../App';
import { hasSentInvite, INVITE_SENT_EVENT } from '../lib/inviteSent';
import '../styles/firstact.css';

/* ---------------------------------------------------------------------------
   First act (card Tw2G4BUmOF5o, sol review finding M1).

   The first minutes used to be a nine-step modal covering the app — reading
   instead of doing, on a screen full of things an unpaired user cannot use
   yet. On arrival the Thread now offers three short choices instead, each one
   a real action on a real surface, and the exhaustive tour stays available on
   demand (More → Take the tour, Help → Take the tour).

   Inline, never modal: it sits at the top of the Thread, so the tabs, the
   composer and the invite card behind it all stay live. It marks itself done
   on the first pick or on "Not now", and never comes back.

   Card E5sHUI8IkN6b (Jonathan email 2026-10-16): once the invite has actually
   left this device — Copy, Share or the emailed link — the card stops offering
   "Invite your partner" and hands over the next intro action instead, and the
   count in the headline is read off the list it renders.
   --------------------------------------------------------------------------- */

/** localStorage flag: the first-act card has been dealt, do not show it again. */
export const FIRST_ACT_KEY = 'pear.firstact';

/** Where the interests quiz lives — same target the nudge and the tour use.
 *  Duplicated rather than imported: InterestsNudgeToast imports FIRST_ACT_KEY
 *  from this file, and a second edge would close the loop. */
const INTERESTS_HASH = '#settings-you';

interface Props {
  /** Solo vs paired: an unpaired user still has someone to invite. */
  paired: boolean;
  /** Dismiss for good (sets the flag). */
  onDismiss: () => void;
  /** Deep link through App's navigateTo — destination AND its composer intent. */
  onGo: (dest: Dest, intent?: ComposeIntent) => void;
}

/** One offer on the card: a label, a line of why, and what the tap does. */
interface Choice {
  id: string;
  name: string;
  why: string;
  run: () => void;
}

export function FirstAct({ paired, onDismiss, onGo }: Props) {
  /* Read at mount, then kept live: a copy made from the invite card below
     this one flips the card over without a reload. */
  const [inviteSent, setInviteSent] = useState(() => hasSentInvite());
  useEffect(() => {
    const sync = () => setInviteSent(hasSentInvite());
    window.addEventListener(INVITE_SENT_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(INVITE_SENT_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const pick = (fn: () => void) => {
    onDismiss();
    fn();
  };

  /* #settings-you opens Settings on the You section (same path the nudge
     takes). The hash is usually empty from Thread, but if it is already there
     no hashchange fires, so say so ourselves rather than do nothing. */
  const goInterests = () => {
    if (window.location.hash === INTERESTS_HASH) {
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } else {
      window.location.hash = INTERESTS_HASH;
    }
  };

  /* The invite only earns its place while there is still somebody to invite
     AND this device has not invited them yet. When it goes, the next unused
     intro action takes the slot — the quiz first, then the question and the
     Notebook, which stay as they are. */
  const showInvite = !paired && !inviteSent;
  const choices: Choice[] = [];

  if (showInvite) {
    choices.push({
      id: 'btn-firstact-invite',
      name: 'Invite your partner',
      why: 'Send the code — the shared space opens when they join.',
      run: () => {
        // The invite card sits directly beneath this one — hand the
        // eye to it rather than off to another screen.
        document
          .querySelector<HTMLElement>('[data-test="invite-card"]')
          ?.scrollIntoView({ block: 'center' });
      },
    });
  } else {
    choices.push({
      id: 'btn-firstact-interests',
      name: 'Set your interests',
      why: 'Four short questions — the concierge words everything better with them done.',
      run: goInterests,
    });
  }

  choices.push(
    {
      id: 'btn-firstact-question',
      name: 'Answer today\u2019s question',
      why: 'You both answer, then the answers reveal together.',
      run: () => onGo({ section: 'us', subsection: 'questions' }, { kind: 'question' }),
    },
    {
      id: 'btn-firstact-note',
      name: 'Save something to the Notebook',
      why: 'A link, a thought, a place — you choose me or us.',
      run: () => onGo({ section: 'notebook' }, { kind: 'note' }),
    },
  );

  /* The headline counts what is actually on offer — three while the invite is
     live, and the same three (quiz in, invite out) once it has been sent. */
  const countWord = ['zero', 'one', 'two', 'three', 'four'][choices.length] ?? String(choices.length);

  return (
    <section
      className="first-act"
      id="first-act"
      aria-labelledby="first-act-title"
      data-testid="first-act"
      data-invite-sent={inviteSent ? '1' : '0'}
    >
      <div className="first-act-head">
        <span className="first-act-eyebrow">To begin</span>
        <button type="button" className="first-act-dismiss" id="btn-firstact-dismiss" onClick={onDismiss}>
          Not now
        </button>
      </div>
      <h2 className="first-act-title" id="first-act-title">
        {`${countWord[0].toUpperCase()}${countWord.slice(1)} ways to start — pick one of them.`}
      </h2>
      <p className="first-act-lede">
        Nothing here needs a manual. One tap, and you are doing the thing rather than
        reading about it. The full tour lives under More whenever you want it.
      </p>

      <ul className="first-act-list">
        {choices.map(c => (
          <li className="first-act-item" key={c.id}>
            <button
              type="button"
              className="first-act-choice"
              id={c.id}
              onClick={() => pick(c.run)}
            >
              <span className="first-act-choice-name">{c.name}</span>
              <span className="first-act-choice-why">{c.why}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
